import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import {
  ArrowRight,
  ArrowsClockwise,
  Check,
  Eye,
  HouseLine,
  LockSimple,
} from "@phosphor-icons/react";
import type { HomeownerStaffData } from "../../types/homeowner";

export default function HomeownerHubOverview({
  data,
  now,
  refreshing,
  onSettings,
  onPublish,
  onInvite,
  onPreview,
  onRefresh,
}: {
  data: HomeownerStaffData;
  now: number;
  refreshing: boolean;
  onSettings: () => void;
  onPublish: () => void;
  onInvite: () => void;
  onPreview: () => void;
  onRefresh: () => void;
}) {
  const shared = data.publications.filter((item) => item.active).length;
  const links = data.invitations.filter(
    (item) => !item.revoked && new Date(item.expires_at).getTime() > now,
  ).length;
  const openRequests = data.requests.filter(
    (item) => !["completed", "cancelled"].includes(item.status),
  ).length;
  const ready = data.enabled && shared > 0 && links > 0;
  const steps = [
    {
      title: "Set up the page",
      done: data.enabled,
      description:
        "Add a welcome message, set the project stage, and turn the page on.",
      detail: data.enabled
        ? "Page is turned on"
        : "Page is off · only your team can see it",
      action: data.enabled ? "Edit page settings" : "Set up homeowner page",
      onClick: onSettings,
    },
    {
      title: "Choose what to share",
      done: shared > 0,
      description:
        "Post a job update or choose photos, documents, and invoices from this job.",
      detail: shared
        ? `${shared} ${shared === 1 ? "item" : "items"} added to the page`
        : "Start with a short welcome or job update",
      action: shared ? "Add another update or file" : "Add your first update",
      onClick: onPublish,
    },
    {
      title: "Give the homeowner access",
      done: links > 0,
      description:
        "Create their private link and access code, then share them with the homeowner.",
      detail: !data.enabled
        ? "Turn on the page first"
        : !data.contacts.some((contact) => contact.email)
          ? "Add an email to the job’s homeowner contact first"
          : links
            ? `${links} unexpired ${links === 1 ? "access link" : "access links"}`
            : "Links are not sent automatically",
      action: "Create access link",
      onClick: onInvite,
    },
  ];
  const next = !data.enabled ? 0 : !shared ? 1 : !links ? 2 : -1;
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "lg",
        overflow: "hidden",
        bgcolor: "background.surface",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: { xs: "none", sm: "grid" },
                placeItems: "center",
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: "md",
                bgcolor: "primary.softBg",
                color: "primary.softColor",
              }}
            >
              <HouseLine size={26} />
            </Box>
            <Box>
              <Typography level="h3">
                Keep your homeowner in the loop
              </Typography>
              <Typography level="body-md" sx={{ mt: 0.75, maxWidth: 760 }}>
                Give them one private page to follow their roofing project, see
                the files you share, and ask your team for help.
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh homeowner page" placement="top">
            <IconButton
              aria-label="Refresh homeowner page"
              variant="plain"
              color="neutral"
              loading={refreshing}
              onClick={onRefresh}
            >
              <ArrowsClockwise size={20} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack
          direction="row"
          gap={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 2 }}
        >
          <Chip
            size="sm"
            color={data.enabled ? "success" : "neutral"}
            variant="soft"
            startDecorator={
              data.enabled ? <Check size={14} /> : <LockSimple size={14} />
            }
          >
            {data.enabled ? "Page is on" : "Page is off"}
          </Chip>
          <Typography level="body-sm">
            {!data.enabled
              ? "Homeowners cannot open this page yet."
              : links
                ? "Available to homeowners with an active link and code."
                : "Create an access link to let a homeowner open this page."}
          </Typography>
          <Button
            size="sm"
            color="neutral"
            variant="outlined"
            startDecorator={<Eye size={18} />}
            onClick={onPreview}
            sx={{ ml: { sm: "auto" } }}
          >
            Preview homeowner page
          </Button>
        </Stack>
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <Typography level="title-lg">
            {ready ? "Your homeowner page is ready" : "Get started in 3 steps"}
          </Typography>
          {ready && (
            <Chip size="sm" color={openRequests ? "warning" : "neutral"}>
              {openRequests
                ? `${openRequests} open ${openRequests === 1 ? "request" : "requests"} below`
                : "No open homeowner requests"}
            </Chip>
          )}
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {steps.map((step, index) => (
            <Box
              key={step.title}
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                border: "1px solid",
                borderColor:
                  next === index ? "primary.outlinedBorder" : "divider",
                bgcolor: "background.surface",
                borderRadius: "md",
                minWidth: 0,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  aria-hidden
                  sx={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    bgcolor: step.done ? "success.softBg" : "primary.softBg",
                    color: step.done
                      ? "success.softColor"
                      : "primary.softColor",
                    fontSize: "sm",
                    fontWeight: "lg",
                  }}
                >
                  {step.done ? <Check size={16} weight="bold" /> : index + 1}
                </Box>
                <Typography level="title-md">{step.title}</Typography>
              </Stack>
              <Typography level="body-sm">{step.description}</Typography>
              <Typography level="body-xs" sx={{ mt: "auto", pt: 0.5 }}>
                {step.detail}
              </Typography>
              {data.can_edit && (
                <Button
                  size="sm"
                  color={next === index ? "primary" : "neutral"}
                  variant={next === index ? "solid" : "outlined"}
                  disabled={
                    index === 2 &&
                    (!data.enabled ||
                      !data.contacts.some((contact) => contact.email))
                  }
                  onClick={step.onClick}
                  endDecorator={
                    next === index ? <ArrowRight size={16} /> : undefined
                  }
                >
                  {step.action}
                </Button>
              )}
            </Box>
          ))}
        </Box>
        <Typography
          level="body-sm"
          startDecorator={<LockSimple size={16} />}
          sx={{ mt: 2, alignItems: "flex-start" }}
        >
          You control what is shared. Internal job notes and files you have not
          selected stay private.
        </Typography>
        {!data.can_edit && (
          <Typography level="body-sm" sx={{ mt: 1 }}>
            You can review this page. A team member with permission to edit the
            job can change it or give the homeowner access.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
